import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Campo de texto que muestra el número con separador de miles mientras
 * se escribe, pero devuelve el valor numérico limpio para guardarlo.
 *
 * Devuelve [valorLimpio, valorMostrado, alCambiar].
 *
 * @param {string|number} valorInicial
 * @param {boolean} esMoneda  true muestra siempre dos decimales
 */
export const useFormattedInput = (valorInicial = "", esMoneda = false) => {
  const formatear = useCallback(
    (numero) =>
      esMoneda
        ? numero.toLocaleString("es-CR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : numero.toLocaleString("es-CR", { maximumFractionDigits: 2 }),
    [esMoneda]
  );

  const [valorMostrado, setValorMostrado] = useState(() => {
    const numero = parseFloat(valorInicial);
    if (!isNaN(numero) && valorInicial !== "") return formatear(numero);
    return String(valorInicial);
  });

  const valorLimpio = useRef(valorInicial);

  // Si el valor inicial cambia desde afuera (por ejemplo al abrir el
  // modal en modo edición), se vuelve a formatear.
  useEffect(() => {
    if (String(valorInicial) === String(valorLimpio.current)) return;

    const numero = parseFloat(valorInicial);
    if (!isNaN(numero) && valorInicial !== "") {
      valorLimpio.current = numero;
      setValorMostrado(formatear(numero));
    } else {
      valorLimpio.current = String(valorInicial);
      setValorMostrado(String(valorInicial));
    }
  }, [valorInicial, formatear]);

  const alCambiar = useCallback(
    (e) => {
      const escrito = e.target.value;

      if (escrito === "") {
        valorLimpio.current = "";
        setValorMostrado("");
        return;
      }

      // En Costa Rica el punto separa miles y la coma los decimales,
      // así que hay que darle vuelta antes de convertir a número.
      const paraConvertir = escrito
        .replace(/\s/g, "")
        .replace(/\./g, "")
        .replace(/,/g, ".");

      const numero = parseFloat(paraConvertir);
      if (isNaN(numero)) {
        valorLimpio.current = "";
        setValorMostrado(escrito);
        return;
      }

      valorLimpio.current = numero;

      // Se formatea solo la parte entera, para no estorbar mientras la
      // persona todavía está escribiendo los decimales.
      const partes = paraConvertir.split(".");
      let mostrado = numero.toLocaleString("es-CR", { maximumFractionDigits: 0 });
      if (partes.length > 1) {
        mostrado += "," + partes[1];
      } else if (esMoneda && escrito.endsWith(",")) {
        mostrado += ",";
      }
      setValorMostrado(mostrado);
    },
    [esMoneda]
  );

  return [valorLimpio.current, valorMostrado, alCambiar];
};
